import type { DeferredLinkResult } from '@apm/tv2m-client-sdk';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 설치 안내 — QR 스캔의 도착지다 (`docs/07` §4 시나리오 C-5).
 *
 * `GET /q/{token}` 이 스캔을 기록하고 `/install?ref=tv2m_qr_{token}` 으로 302 한다.
 * **그 `ref` 는 이미 저장소에 들어갔다** — `useTv2m` 이 화면을 고르기 전에
 * `sdk.route()` 를 부르고, 그것이 설치 ref 를 보관한다. 사용자가 여기서 홈 화면에
 * 추가(A2HS)한 뒤 아이콘으로 다시 들어오면 시작 URL 에 `ref` 가 없는데(manifest 의
 * `start_url`), 같은 오리진의 저장소는 그 사이에도 유지된다.
 *
 * **그런데 첫 실행이 아니면 복원이 일어나지 않는다.** 이미 이 앱을 쓴 적 있는 기기가
 * QR 을 찍은 경우다 — 그때는 설치할 것이 없으므로 "지금 확인" 버튼으로 그 자리에서
 * 조회를 돌린다.
 */
export function InstallPage({
  firstRun,
  deferred,
  onCheckNow,
  checking,
}: {
  firstRun: boolean;
  /** 첫 실행에서 이미 조회했으면 그 결과. `null` 이면 아직 조회하지 않았다 */
  deferred: DeferredLinkResult | null;
  onCheckNow: () => void;
  checking: boolean;
}) {
  const t = useT();
  // 조회했는데 못 찾았다 — QR 이 만료됐거나(5분) 다른 가구에서 스캔했거나 이미 소비됐다.
  const notFound = deferred !== null && !deferred.found;

  return (
    <>
      <div className="card">
        <h2 className="card__title">{t('install.title')}</h2>
        <p className="card__text">{t('install.text')}</p>
        <ol className="steps">
          <li>{t('install.step1')}</li>
          <li>{t('install.step2')}</li>
          <li>{t('install.step3')}</li>
        </ol>
      </div>

      {notFound && (
        <div className="card">
          <h2 className="card__title">{t('install.notRestoredTitle')}</h2>
          <p className="card__text">{t('install.notRestoredText')}</p>
        </div>
      )}

      {/*
        첫 실행이면 `useTv2m` 이 이미 조회를 마쳤다. 두 번째 이후의 방문에서만 이
        버튼이 뜻을 갖는다 — 그때는 아무도 조회를 걸지 않기 때문이다.
      */}
      {!firstRun && (
        <button type="button" className="button" onClick={onCheckNow} disabled={checking}>
          {checking ? t('install.checking') : t('install.checkNow')}
        </button>
      )}
    </>
  );
}
